<template>
  <div class="contest-detail-wrapper">
    <div class="container">
      <button v-if="!submissionLimitReached && isOpen && isOrgMember" class="button-primary mb-10 mr-10"
              @click="goToSubmit">
        Submit image
      </button>
      <button class="button-secondary mb-10 mr-10" @click="$router.back()">
        <span>
          Back
        </span>
      </button>
      <button v-if="isContestOwner && isOpen" class="button-warning mb-10" @click="closeContest">
        Close contest
      </button>
      <div v-if="showImageDetail" class="image-detail-overlay" @click.self="closeImageDetail">
        <ContestImageDetail :id="selectedImageId" :contest_id="id" :voted="userVoted" @close="closeImageDetail"
                            @next="goToNextImage" @last="goToPreviousImage" @vote="submitVote"/>
      </div>
      <div class="contest-detail-page-container">
        <div class="contest-detail-main-content">
          <div class="card w-100" v-if="contest">
            <h3>Contest: {{ contest.contest.name }}</h3>
            <div v-if="isFinished" class="contest-podium-container mb-30 border-bottom-2">
              <div class="contest-podium mb-30">
                <div class="podium-step-container">
                  <img v-if="secondPlaceImageUrl" :src="secondPlaceImageUrl" :alt="id"
                       class="contest-podium-image second-place-image">
                  <div class="podium-second-place-step podium-step"></div>
                </div>
                <div class="podium-step-container">
                  <img v-if="firstPlaceImageUrl" :src="firstPlaceImageUrl" :alt="id"
                       class="contest-podium-image first-place-image">
                  <div class="podium-first-place-step podium-step"></div>
                </div>
                <div class="podium-step-container">
                  <img v-if="thirdPlaceImageUrl" :src="thirdPlaceImageUrl" :alt="id"
                       class="contest-podium-image third-place-image">
                  <div class="podium-third-place-step podium-step"></div>
                </div>
              </div>
            </div>
            <div v-if="imageIds" class="contest-detail-images-display">
              <ContestImagePreview v-for="imgId in imageIds" :id="imgId" :contest_id="id" :voted="userVoted"
                                   @open="openImageDetail" @vote="submitVote"/>
            </div>
          </div>
        </div>
        <div class="contest-detail-sidebar">
          <div v-if="contest" class="card w-100">
            <h3>Details</h3>
            <span v-if="isOpen" class="status-open-pill">Open</span>
            <span v-else class="status-closed-pill">Closed</span>
            <p><i>Created by {{ contest.creator_username }}</i></p>
            <p>{{ contest.contest.description }}</p>
            <p><strong>{{ contest.votes }}</strong> votes casted.</p>
            <p><strong>{{ contest.submissions }}</strong> images submitted.</p>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script setup>
import apiService from '@/services/apiService';
import {onMounted, ref} from 'vue';
import ContestImagePreview from '../common/ContestImagePreview.vue';
import {useRouter} from 'vue-router';
import ContestImageDetail from '../common/ContestImageDetail.vue';


const {id} = defineProps({
  id: String
})

const router = useRouter();

const contest = ref(null);
const imageIds = ref(null);
const imageIdsAndVotes = ref([]);
const user_subs = ref(0);
const submissionLimitReached = ref(false);
const isOpen = ref(false);
const isFinished = ref(false);

const selectedImageId = ref(null);
const showImageDetail = ref(false);

const firstPlaceImageUrl = ref(null);
const secondPlaceImageUrl = ref(null);
const thirdPlaceImageUrl = ref(null);
const isContestOwner = ref(false);
const isOrgMember = ref(false);

const userVoted = ref(false);

onMounted(async () => {
  await loadData();
})

async function loadData() {
  const contest_res = await apiService.getContestById(id);
  contest.value = contest_res;

  const id_res = await apiService.getImageIdsInContest(id);
  imageIds.value = id_res;

  const user_sub_res = await apiService.getNumberOfSubmissionsPerUser(id);
  user_subs.value = user_sub_res;

  const voted_res = await apiService.getUserVoteStatus(id);
  userVoted.value = voted_res.voted;

  const imagesAndVotesRes = await apiService.getImageIdsInContestWithVotes(id);
  imageIdsAndVotes.value = imagesAndVotesRes.sort((a, b) => b.votes - a.votes);

  isOpen.value =
      Date.parse(contest.value.contest.end_date) > Date.now() &&
      Date.parse(contest.value.contest.start_date) < Date.now();

  isFinished.value = Date.parse(contest.value.contest.end_date) < Date.now();

  const userId = localStorage.getItem('userId');
  isContestOwner.value = contest.value.contest.creator_id === userId;

  if (contest.value.contest.org_id === null) {
    isOrgMember.value = true;
  } else {
    isOrgMember.value = contest.value.contest.org_id === getUserOrgId();
  }

  if (isFinished.value) {
    await getFirstPlace();
    await getSecondPlace();
    await getThirdPlace();
  }
}

function goToSubmit() {
  router.push(`/contests/${contest.value.contest.id}/submit`)
}

function openImageDetail(imageId) {
  selectedImageId.value = imageId;
  showImageDetail.value = true;
}

function closeImageDetail() {
  showImageDetail.value = false;
  selectedImageId.value = null;
}

function goToNextImage() {
  if (!imageIds.value || selectedImageId.value === null) return

  const currentIndex = imageIds.value.indexOf(selectedImageId.value)

  if (currentIndex === -1) return

  const nextIndex = (currentIndex + 1) % imageIds.value.length
  selectedImageId.value = imageIds.value[nextIndex]
}

function goToPreviousImage() {
  if (!imageIds.value || selectedImageId.value === null) return

  const currentIndex = imageIds.value.indexOf(selectedImageId.value)

  if (currentIndex === -1) return

  const prevIndex =
      (currentIndex - 1 + imageIds.value.length) % imageIds.value.length

  selectedImageId.value = imageIds.value[prevIndex]
}

async function submitVote(image_id) {
  const res = await apiService.submitVoteForImage(image_id, id);
  await loadData();
}

async function getFirstPlace() {
  if (imageIdsAndVotes.value.length >= 1) {
    const response = await apiService.getImageSignedUrl(imageIdsAndVotes.value[0].image_id);
    firstPlaceImageUrl.value = response.url;
  }
}

async function getSecondPlace() {
  if (imageIdsAndVotes.value.length >= 2) {
    let response = await apiService.getImageSignedUrl(imageIdsAndVotes.value[1].image_id);
    secondPlaceImageUrl.value = response.url;
  }
}

async function getThirdPlace() {
  if (imageIdsAndVotes.value.length >= 3) {
    let response = await apiService.getImageSignedUrl(imageIdsAndVotes.value[2].image_id);
    thirdPlaceImageUrl.value = response.url;
  }
}

async function closeContest() {
  let closed = await apiService.closeContest(id);
  if (closed) {
    window.location.reload();
  }
}

function getUserOrgId() {
  return localStorage.getItem('org_id');
}


</script>

<style scoped>
.contest-detail-wrapper {
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  color: #323232;
}

.contest-detail-page-container {
  display: flex;
  height: 100%;
  width: 100%;
  align-items: start;
  justify-content: start;
}

.contest-detail-main-content {
  width: 70%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-right: 1rem;
}

.contest-detail-sidebar {
  width: 30%;
}

.card {
  min-height: 50vh;
}

.contest-detail-images-display {
  display: flex;
  flex-wrap: wrap;
  justify-content: start;
  align-items: center;
  width: 100%;
  gap: 1rem;
}

.image-detail-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1000;

  display: flex;
  align-items: center;
  justify-content: center;
}

.contest-podium-container {
  height: 25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.contest-podium {
  width: 80%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 0.5rem;
}

.podium-step-container {
  width: 30%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.podium-step {
  background-color: lightgray;
  width: 100%;
}

.podium-second-place-step {
  height: 30%;
}

.podium-first-place-step {
  height: 50%;
}

.podium-third-place-step {
  height: 20%;
}

.contest-podium-image {
  max-width: 100%;
  max-height: 30%;
  border-radius: 5px;
}

.status-open-pill {
  font-size: 1rem;
  background-color: #009500;
  color: white;
  padding: 3px;
  border: 1px solid black;
}

.status-closed-pill {
  font-size: 1rem;
  background-color: #d99c33;
  color: white;
  padding: 3px;
  border-radius: 10px;
}
</style>